import {
  get,
  patch,
  put,
  uploadFile,
  post,
  deleteApi,
  postForFileAsResponse,
} from "../../api/index";
import store from "../../store";

import { API_ENDPOINTS, DOCUMENTS } from "../../constants";
import {
  getContentForComponent,
  getComponentPosition,
} from "../../utils/documentUtils";

import { formatClauseOption } from "./templateMethod";
import {
  updateDocSaveIndicator,
  updateComponentsDBRowColIndexesAction,
} from "../DocumentActions/method";

const { APIS } = API_ENDPOINTS;

const { DOCUMENT_MODE } = DOCUMENTS;

export const fetchDocumentTemplates = (
  pageNum = 1,
  withHeaders = false,
  filters,
) =>
  new Promise((resolve, reject) => {
    let query = "";
    if (pageNum && withHeaders) {
      query = `?page_num=${pageNum}`;
    }
    if (filters) {
      query = `${query}&template_status=${filters}`;
    }
    get(`${APIS.DOCUMENT_TEMPLATES}${query}`, withHeaders)
      .then(response => {
        if (withHeaders) {
          resolve(response);
        } else {
          resolve(response.Templates);
        }
      })
      .catch(error => {
        reject(error);
      });
  });

export const fetchDocTemplateById = (
  typeId,
  pageNum = 1,
  withHeaders = false,
) =>
  new Promise((resolve, reject) => {
    let query = "?status=ACTIVE";
    if (typeId) {
      query += `&type_id=${typeId}`;
    }
    if (pageNum && withHeaders) {
      query += `&page_num=${pageNum}`;
    }
    get(`${APIS.DOCUMENT_TEMPLATES}${query}`, withHeaders)
      .then(response => {
        if (withHeaders) {
          resolve(response);
        } else {
          resolve(response.Templates);
        }
      })
      .catch(error => {
        reject(error);
      });
  });

export const updatePDFFormFieldServiceTemplate = (docId, updatedFields) =>
  new Promise((resolve, reject) => {
    store.dispatch(updateDocSaveIndicator("SAVING"));
    post(`${APIS.PDF_TEMPLATE_FORM_FIELDS}/${docId}`, updatedFields)
      .then(response => {
        store.dispatch(updateDocSaveIndicator("SAVED"));
        resolve(response);
      })
      .catch(error => {
        store.dispatch(updateDocSaveIndicator("SAVE_FAILED"));
        console.debug(
          `assetServices => getAssetFolderTreeService => Error : ${error}`,
        );
        reject(error);
      });
  });

export const updateTemplateSignatories = (signerData, docId) =>
  new Promise((resolve, reject) => {
    post(`${APIS.TEMPLATE_SIGNATORY}/${docId}`, signerData)
      .then(response => resolve(response))
      .catch(error => reject(error));
  });

export const deleteTemplateSigner = (signerData, docId) =>
  new Promise((resolve, reject) => {
    deleteApi(`${APIS.TEMPLATE_SIGNATORY}/${docId}`, signerData)
      .then(response => resolve(response))
      .catch(error => reject(error));
  });

export const uploadPDFTemplate = data =>
  new Promise((resolve, reject) => {
    const formData = new FormData();
    formData.append("file", data.data);
    formData.append("folder_id", data.folderId);
    uploadFile(`${APIS.UPLOAD_PDF_TEMPLATE}`, formData)
      .then(res => {
        if (res && !res.Template && !res.Template.id) {
          reject(new Error("Invalid response after uploading the pdf"));
        }
        resolve(res.Template.id);
      })
      .catch(error => {
        reject(error);
      });
  });

export const createTemplate = (templateDetails, docDetails) =>
  new Promise((resolve, reject) => {
    const formData = new FormData();
    formData.append("doc", docDetails);
    formData.append("data", JSON.stringify(templateDetails));
    uploadFile(`${APIS.DOCUMENT_TEMPLATES}`, formData)
      .then(res => {
        resolve(res);
      })
      .catch(error => {
        reject(error);
      });
  });

export const fetchQuotetermTemplateByID = templateID =>
  new Promise((resolve, reject) => {
    get(`${APIS.DOCUMENT_TEMPLATES}${templateID}`)
      .then(response => {
        if (!response.Template) {
          reject(new Error("Template unavailable for given id"));
        }
        resolve(response.Template);
      })
      .catch(error => {
        reject(error);
      });
  });

export const switchTemplateSections = templateID =>
  new Promise((resolve, reject) => {
    const content = {
      id: templateID,
    };
    post(`${APIS.TOGGLE_DOCUMENT_TEMPLATE}/${templateID}`, content)
      .then(res => {
        resolve(res);
      })
      .catch(error => {
        reject(error);
      });
  });

export const deleteTemplateById = templateID =>
  new Promise((resolve, reject) => {
    const content = {
      id: templateID,
    };
    deleteApi(`${APIS.DELETE_DOCUMENT_TEMPLATE}/${templateID}`, content)
      .then(res => {
        resolve(res);
      })
      .catch(error => {
        reject(error);
      });
  });

export const saveClauseInformation = (clauseName, clauseID, clauseContent) =>
  new Promise((resolve, reject) => {
    const content = {
      name: clauseName,
      ID: clauseID,
      content: {
        clauses: clauseContent,
      },
    };
    post(`${APIS.DOCUMENT_CLAUSE}`, content)
      .then(res => {
        resolve(res);
      })
      .catch(error => {
        reject(error);
      });
  });

export const addTemplateComponentService = (templateID, component) =>
  new Promise((resolve, reject) => {
    store.dispatch(updateDocSaveIndicator("SAVING"));
    post(`${APIS.TEMPLATE_V2}/${templateID}/components`, {
      component_displacements: component,
      field_ids: null,
    })
      .then(res => {
        store.dispatch(updateDocSaveIndicator("SAVED"));
        if (Array.isArray(res) && res.length > 0) {
          store.dispatch(updateComponentsDBRowColIndexesAction(res));
        }
        resolve(res);
      })
      .catch(err => {
        store.dispatch(updateDocSaveIndicator("SAVE_FAILED"));
        reject(err);
      });
  });

export const deleteTemplateComponentService = (templateID, component) =>
  new Promise((resolve, reject) => {
    store.dispatch(updateDocSaveIndicator("SAVING"));
    // get old section
    const state = store.getState();
    const deletedFields = [];
    const { fieldSectionMapping } = state.TemplateStore;
    const { fieldDiffList: { deleted = [] } = {} } = component;
    const componentId = component.id;
    deleted.forEach(fieldID => {
      if (
        !(fieldID in fieldSectionMapping) ||
        (fieldID in fieldSectionMapping &&
          fieldSectionMapping[fieldID].filter(f => f !== componentId).length ===
            0)
      ) {
        deletedFields.push(fieldID);
      }
    });

    // request payload
    const content = {
      id: componentId,
      field_ids: {
        deleted: [...deletedFields],
      },
    };

    deleteApi(`${APIS.TEMPLATE_V2}/${templateID}/components`, content)
      .then(res => {
        store.dispatch(updateDocSaveIndicator("SAVED"));
        if (Array.isArray(res) && res.length > 0) {
          store.dispatch(updateComponentsDBRowColIndexesAction(res));
        }
        resolve(res);
      })
      .catch(err => {
        store.dispatch(updateDocSaveIndicator("SAVE_FAILED"));
        reject(err);
      });
  });

export const restoreTemplateComponentService = (
  templateID,
  component,
  layout,
) => {
  const state = store.getState();
  const { fieldSectionMapping } = state.TemplateStore;

  const { rowIndex, colIndex } = getComponentPosition(component.id, layout);
  if (!(rowIndex === undefined || colIndex === undefined)) {
    const { fieldDiffList: { added = [] } = {} } = component;

    const addedFields = [];
    const deletedFields = [];
    // const sectionId = component.id;

    added.forEach(fieldID => {
      if (!(fieldID in fieldSectionMapping)) {
        addedFields.push(fieldID);
      }
    });

    const componentPayload = {
      component_displacements: {
        component: {
          content: getContentForComponent(component, DOCUMENT_MODE.DOCUMENT),
          row: rowIndex,
          col: colIndex,
          component_id: component.id,
        },
        new_row_added: layout[rowIndex].length === 1,
      },
      field_ids: {
        added: addedFields,
        deleted: deletedFields,
      },
    };
    return new Promise((resolve, reject) => {
      put(
        `${APIS.TEMPLATE_V2}/${templateID}/component_restore`,
        componentPayload,
      )
        .then(res => {
          if (Array.isArray(res) && res.length > 0) {
            store.dispatch(updateComponentsDBRowColIndexesAction(res));
          }
          resolve(res);
        })
        .catch(err => {
          reject(err);
        });
    });
  }
  return null;
};

export const updateTemplateComponentService = section =>
  new Promise((resolve, reject) => {
    store.dispatch(updateDocSaveIndicator("SAVING"));

    const state = store.getState();
    const { fieldSectionMapping } = state.TemplateStore;

    const newComponent = getContentForComponent(
      section,
      DOCUMENT_MODE.TEMPLATE,
    );
    newComponent.section_id = section.id;
    newComponent.section_type = section.type || section.section_type;
    newComponent.section_name = section.header || section.section_name;
    newComponent.source = "DRAFT-JS";

    delete newComponent.currentContent;

    const { fieldDiffList: { added = [], deleted = [] } = {} } = section;

    const addedFields = [];
    const deletedFields = [];
    const sectionId = section.id;

    added.forEach(fieldID => {
      if (!(fieldID in fieldSectionMapping)) {
        addedFields.push(fieldID);
      }
    });

    deleted.forEach(fieldID => {
      if (
        !(fieldID in fieldSectionMapping) ||
        (fieldID in fieldSectionMapping &&
          fieldSectionMapping[fieldID].filter(f => f !== sectionId).length ===
            0)
      ) {
        deletedFields.push(fieldID);
      }
    });
    // request payload
    const updateComponentDetails = {
      component: {
        content: newComponent,
      },
      field_ids: {
        added: addedFields,
        deleted: deletedFields,
      },
    };

    const componentURL = `${APIS.TEMPLATE_COMPONENTS}/${newComponent.id}`;

    put(componentURL, updateComponentDetails)
      .then(res => {
        store.dispatch(updateDocSaveIndicator("SAVED"));
        resolve(res.Component);
      })
      .catch(err => {
        store.dispatch(updateDocSaveIndicator("SAVE_FAILED"));
        reject(err);
      });
  });

export const saveTemplateComponent = templateID =>
  new Promise((resolve, reject) => {
    store.dispatch(updateDocSaveIndicator("SAVING"));
    const state = store.getState();
    const { templateIdOrder } = state.TemplateStore;
    const content = {
      order: templateIdOrder,
    };
    post(`${APIS.TEMPLATE_COMPONENTS}/templates/${templateID}/`, content)
      .then(res => {
        store.dispatch(updateDocSaveIndicator("SAVED"));
        resolve(res);
      })
      .catch(error => {
        store.dispatch(updateDocSaveIndicator("SAVE_FAILED"));
        reject(error);
      });
  });

export const updateTemplateOrder = payload =>
  new Promise((resolve, reject) => {
    store.dispatch(updateDocSaveIndicator("SAVING"));
    const state = store.getState();
    const { template } = state.TemplateStore;

    if (!(template && payload)) {
      reject(Error("No document available, or incorrect order"));
    }
    if (!payload.old_loc) {
      const { components } = state.DocumentStore;
      const component = components[payload.component.id];
      payload = {
        ...payload,
        old_loc: {
          row: component.dbRowIndex,
          col: component.dbColIndex,
        },
      };
    }
    put(`${APIS.TEMPLATE_V2}/${template.id}/component_layout`, payload)
      .then(res => {
        if (Array.isArray(res) && res.length > 0) {
          store.dispatch(updateComponentsDBRowColIndexesAction(res));
        }
        store.dispatch(updateDocSaveIndicator("SAVED"));
        resolve(payload.old_loc);
      })
      .catch(error => {
        store.dispatch(updateDocSaveIndicator("SAVE_FAILED"));
        reject(error);
      });
  });

export const fetchDynamicFields = () =>
  new Promise((resolve, reject) => {
    get(`${APIS.DOC_FIELDS}`)
      .then(res => {
        resolve(res.fields);
      })
      .catch(error => {
        reject(error);
      });
  });

export const fetchDocCustomField = () =>
  new Promise((resolve, reject) => {
    get(`${APIS.DOC_FIELDS_TYPES}`)
      .then(response => {
        resolve(response);
      })
      .catch(error => {
        reject(error);
      });
  });

export const createCustomDynamicField = data =>
  new Promise((resolve, reject) => {
    post(`${APIS.DOC_FIELDS}`, data)
      .then(res => {
        resolve(res);
      })
      .catch(error => {
        reject(error);
      });
  });

export const updateCustomDynamicField = (id, data) =>
  new Promise((resolve, reject) => {
    put(`${APIS.DOC_FIELDS}${id}`, data)
      .then(res => {
        resolve(res);
      })
      .catch(error => {
        reject(error);
      });
  });

export const createClauseOptions = clause =>
  new Promise((resolve, reject) => {
    const clauseOption = formatClauseOption(clause);
    post(`${APIS.CLAUSE_OPTIONS}`, clauseOption)
      .then(res => resolve(res.field))
      .catch(err => reject(err.response));
  });

export const updateClauseOptionsService = clause =>
  new Promise((resolve, reject) => {
    const clauseOption = formatClauseOption(clause);
    put(`${APIS.CLAUSE_OPTIONS}${clause.id}`, clauseOption)
      .then(res => resolve(res.field))
      .catch(err => reject(err.response));
  });

export const updateTemplateSectionLabelService = (id, label) =>
  new Promise((resolve, reject) => {
    const state = store.getState();
    const {
      template: {
        content: { sections },
      },
    } = state.TemplateStore;
    const componentDetails = sections.find(section => section.id === id);
    componentDetails.label = label;
    updateTemplateComponentService(componentDetails)
      .then(res => {
        resolve(res);
      })
      .catch(err => {
        reject(err);
      });
  });

// Template workflow related services

export const createTempWorkFlowService = payload =>
  new Promise((resolve, reject) => {
    post(APIS.WORK_FLOW, payload)
      .then(response => {
        resolve(response.Workflow);
      })
      .catch(error => {
        reject(error);
      });
  });

export const updateTemplateWFConditionService = (id, payload) =>
  new Promise((resolve, reject) => {
    put(`${APIS.WORK_FLOW}/${id}/rules`, payload)
      .then(response => {
        resolve(response.rules);
      })
      .catch(error => {
        reject(error);
      });
  });

export const getTempWorkFlowDetailsService = id =>
  new Promise((resolve, reject) => {
    get(`${APIS.WORK_FLOW}/${id}`)
      .then(response => {
        resolve(response.Workflow);
      })
      .catch(error => {
        reject(error);
      });
  });

export const removeRuleFromTemplateWFService = ruleId =>
  new Promise((resolve, reject) => {
    deleteApi(`${APIS.WORK_FLOW_RULES}/${ruleId}`)
      .then(response => {
        resolve(response);
      })
      .catch(error => {
        reject(error);
      });
  });

export const removeWorkFlowFromTemplateService = workflowId =>
  new Promise((resolve, reject) => {
    deleteApi(`${APIS.WORK_FLOW}/${workflowId}`)
      .then(response => {
        resolve(response);
      })
      .catch(error => {
        reject(error);
      });
  });

export const cloneTemplateService = (id, name) =>
  new Promise((resolve, reject) => {
    post(`${APIS.DOCUMENT_TEMPLATES}${id}/clone`, { template_name: name })
      .then(response => {
        resolve(response);
      })
      .catch(error => {
        reject(error);
      });
  });

export const changeTemplateStatusService = (id, payload) =>
  new Promise((resolve, reject) => {
    put(`${APIS.DOCUMENT_TEMPLATES}${id}`, payload)
      .then(res => {
        resolve(res);
      })
      .catch(err => {
        reject(err);
      });
  });

export const updateTemplateTitle = (id, payload) =>
  new Promise((resolve, reject) => {
    store.dispatch(updateDocSaveIndicator("SAVING"));
    patch(`${APIS.DOCUMENT_TEMPLATES}${id}`, payload)
      .then(response => {
        store.dispatch(updateDocSaveIndicator("SAVED"));
        resolve(response);
      })
      .catch(error => {
        store.dispatch(updateDocSaveIndicator("SAVE_FAILED"));
        reject(error);
      });
  });

export const updateTemplateDetailsService = payload =>
  new Promise((resolve, reject) => {
    store.dispatch(updateDocSaveIndicator("SAVING"));
    patch(`${APIS.DOCUMENT_TEMPLATES}${payload.docId}`, payload.data)
      .then(res => {
        store.dispatch(updateDocSaveIndicator("SAVED"));
        resolve(res.Template);
      })
      .catch(error => {
        store.dispatch(updateDocSaveIndicator("SAVE_FAILED"));
        console.error(
          `DocumentActions => services => updateTemplateDetailsService => ${error}`,
        );
        reject(error);
      });
  });

export const updatetemplateTagService = (templateId, tagId) =>
  new Promise((resolve, reject) => {
    put(`${APIS.TEMPLATE_TAG}/${templateId}/tag/${tagId}`)
      .then(res => {
        resolve(res);
      })
      .catch(error => {
        reject(error);
      });
  });

/*
==========================================
        Global Templates Services
==========================================
*/
export const getGlobalTemplates = () =>
  new Promise((resolve, reject) => {
    get(`${APIS.GLOBAL_TEMPLATES}`)
      .then(response => {
        resolve(response && response.Templates);
      })
      .catch(error => {
        reject(error);
      });
  });

export const fetchDocJSONService = globalTemplate =>
  new Promise((resolve, reject) => {
    post(`${APIS.GLOBAL_TEMPLATES_DEMO}/${globalTemplate}/docs`)
      .then(response => {
        resolve(response);
      })
      .catch(error => {
        console.debug(
          `templateServices => fetchDocJSONService => Error : ${error}`,
        );
        reject(error);
      });
  });

export const createDocFromGlobalTemplates = templateID =>
  new Promise((resolve, reject) => {
    post(`${APIS.CREATE_NEW_DOCUMENT_FROM_GLOBAL_TEMPLATE}/${templateID}/docs`)
      .then(response => {
        resolve(response && response.Document);
      })
      .catch(error => {
        reject(error);
      });
  });

export const cloneGlobalTemplate = templateID =>
  new Promise((resolve, reject) => {
    post(`${APIS.CLONE_GLOBAL_TEMPLATE}/${templateID}`)
      .then(response => {
        resolve(response);
      })
      .catch(error => {
        reject(error);
      });
  });

export const getTemplateSearchSuggestions = (keyword, global) =>
  new Promise((resolve, reject) => {
    get(
      `${
        APIS.TEMPLATE_SEARCH_SUGGESTIONS
      }?search=${keyword}&global=${global.toString()}`,
    )
      .then(response => {
        resolve(response);
      })
      .catch(error => {
        reject(error);
      });
  });

export const getTemplateSearchContent = (keyword, global, page = 0, filters) =>
  new Promise((resolve, reject) => {
    const filterParams = filters ? `&template_status=${filters}` : "";
    get(
      `${
        APIS.TEMPLATE_SEARCH_CONTENT
      }?search=${keyword}&global=${global.toString()}&page=${page}${filterParams}`,
    )
      .then(response => {
        resolve(response);
      })
      .catch(error => {
        reject(error);
      });
  });

export const generatePermaLinkTemplateService = payload =>
  new Promise((resolve, reject) => {
    const blob = new Blob([payload.file], { type: "plain/html" });
    const formData = new FormData();
    formData.append("template_id", payload.template_id);
    formData.append("file", blob, payload.fileName);
    formData.append("css", payload.css);
    uploadFile(`${APIS.TEMPLATES_PUBLISH}`, formData)
      .then(res => {
        resolve(res);
      })
      .catch(error => {
        reject(error);
      });
  });

export const deleteStripeFromTemplate = (stripeData, tempID) =>
  new Promise((resolve, reject) => {
    deleteApi(`${APIS.STRIPE_AUTH}/temp-buttons/${tempID}`, stripeData)
      .then(response => resolve(response))
      .catch(error => reject(error));
  });

export const saveDocumentAsTemplateService = (docId, templateName) =>
  new Promise((resolve, reject) => {
    post(`${APIS.DOC_AS_TEMPLATE}/${docId}/as-temlpate`, templateName)
      .then(res => {
        resolve(res);
      })
      .catch(error => {
        console.error(
          `saveDocumentAsTemplateService => saveDocumentAsTemplateService=> ${error}`,
        );
        reject(error);
      });
  });

export const batchUpdateComponentTemplateService = (docId, components) =>
  new Promise((resolve, reject) => {
    put(
      `${APIS.BATCH_UPDATE_TEMPLATE}/${docId}/template-components`,
      components,
    )
      .then(res => {
        resolve(res);
      })
      .catch(error => {
        console.error(
          `batchUpdateComponentTemplateService => batchUpdateComponentTemplateService=> ${error}`,
        );
        reject(error);
      });
  });

export const assignUserGroupToTemplateService = (templateId, data) =>
  new Promise((resolve, reject) => {
    post(`${APIS.DOCUMENT_TEMPLATES}${templateId}/usergroups`, data)
      .then(res => {
        resolve(res);
      })
      .catch(error => {
        console.error(
          `assignUserGroupToTemplateService => assignUserGroupToTemplateService=> ${error}`,
        );
        reject(error);
      });
  });

export const searchDocumentTemplates = (query, pageNum) =>
  new Promise((resolve, reject) => {
    get(
      `${APIS.DOCUMENT_TEMPLATES}?search=${query}&status=ACTIVE&page_num=${pageNum}`,
      true,
    ) // Pass true bool flag to return the response with headers
      .then(response => {
        resolve(response);
      })
      .catch(error => {
        reject(error);
      });
  });

export const assignRecipientTypeService = (templateID, payload) =>
  new Promise((resolve, reject) => {
    post(`${APIS.TEMPLATE_RECIPIENT_TYPE}${templateID}`, payload)
      .then(response => {
        resolve(response);
      })
      .catch(error => {
        reject(error);
      });
  });

export const downloadTemplateCsvService = (templateID, payload) =>
  new Promise((resolve, reject) => {
    post(`${APIS.DOCS_CSV_TEMPLATE}${templateID}${payload}`)
      .then(response => {
        resolve(response);
      })
      .catch(error => {
        reject(error);
      });
  });

export const createTemplateSignatoriesService = (templateID, data) =>
  new Promise((resolve, reject) => {
    post(`${APIS.DOC_TEMPLATE_SIGNATORY}${templateID}`, data)
      .then(res => {
        resolve(res);
      })
      .catch(err => {
        console.error(
          `documentServices => createTemplateSignatories => ${err}`,
        );
        reject(err);
      });
  });

export const deleteTemplateSignatoriesService = (templateID, data) =>
  new Promise((resolve, reject) => {
    deleteApi(`${APIS.DOC_TEMPLATE_SIGNATORY}${templateID}`, data)
      .then(res => {
        resolve(res);
      })
      .catch(err => {
        console.error(
          `documentServices => createTemplateSignatories => ${err}`,
        );
        reject(err);
      });
  });

export const downloadTemplateJsonDataService = templateId =>
  new Promise((resolve, reject) => {
    postForFileAsResponse(`${APIS.TEMPLATES_JSON}/${templateId}/swagger-schema`)
      .then(res => {
        resolve(res);
      })
      .catch(err => {
        console.error(
          `templateServices => downloadTemplateJsonDataService => ${err}`,
        );
        reject(err);
      });
  });

export const getAllConditionalList = (templateID, fieldID) =>
  new Promise((resolve, reject) => {
    get(`${APIS.DOC_TEMPLATE_CONDITIONAL}/${templateID}/fields/${fieldID}`)
      .then(res => {
        resolve(res);
      })
      .catch(err => {
        console.error(`templateServices => getAllConditionalList => ${err}`);
        reject(err);
      });
  });

export const addLogicToFieldsInTemplateService = payload =>
  new Promise((resolve, reject) => {
    post(`${APIS.DOC_TEMPLATE_CONDITIONAL}`, payload)
      .then(res => {
        resolve(res);
      })
      .catch(err => {
        reject(err);
      });
  });

export const removeLogicForFieldService = (templateID, fieldID) =>
  new Promise((resolve, reject) => {
    deleteApi(`${APIS.DOC_TEMPLATE_CONDITIONAL}/${templateID}`, [fieldID])
      .then(res => {
        resolve(res);
      })
      .catch(err => {
        reject(err);
      });
  });
